import { navigate } from '@mr-yum/frontend-core/dist/services/routes'
import {
  cn,
  DiscountOutlineIcon,
  DrawerContainer,
  useBreakpoint,
  UserCircleOutlineIcon,
  UserFilledIcon,
} from '@mr-yum/frontend-ui'
import React, { useCallback, useState } from 'react'

import { useLoyaltyPoints } from '@/components/Rewards/hooks/useLoyaltyPoints'
import { useLoggerLegacy } from '@/hooks/useLogger'
import { routes } from '@/lib/routes'
import { formatPoints } from '@/lib/utils'

import { GuestProfileMenu } from './GuestProfileMenu'

interface Props {
  venueId: string
  venueSlug: string
  venueName: string
  className?: string
  isUserFilledIcon?: boolean
}

export const ProfileNavBarIcon = ({
  venueSlug,
  venueId,
  venueName,
  className,
  isUserFilledIcon,
}: Props) => {
  const { logEventLegacy } = useLoggerLegacy()
  const [open, setOpen] = useState(false)
  const breakpoint = useBreakpoint()
  const isMobile = ['sm', 'md'].includes(breakpoint)
  const { points } = useLoyaltyPoints({ venueSlug })

  const handleOpen = useCallback(async () => {
    logEventLegacy('Clicked on guest profile menu')

    if (isMobile) {
      await navigate(routes.guestProfile, {
        venueSlug,
      })
    } else {
      setOpen(true)
    }
  }, [isMobile, logEventLegacy, venueSlug])

  const handleClose = useCallback(() => {
    logEventLegacy('Closed guest profile menu')
    setOpen(false)
  }, [logEventLegacy])

  if (points) {
    return (
      <>
        <button
          onClick={handleOpen}
          className="inline-flex flex-shrink-0 items-center justify-center gap-1.5 rounded-md border border-white/20 py-[7px] pl-3 pr-4"
          aria-label="View account"
        >
          <DiscountOutlineIcon
            width={20}
            height={20}
            className="flex-shrink-0"
          />

          <span
            className="my-label-sm"
            data-testid="profile-navbar-loyalty-points"
          >
            {formatPoints(points)}
          </span>
        </button>

        <DrawerContainer isOpen={open} onClose={handleClose} anchor="right">
          <GuestProfileMenu
            venueSlug={venueSlug}
            venueId={venueId}
            venueName={venueName}
            handleClose={handleClose}
          />
        </DrawerContainer>
      </>
    )
  }

  return (
    <>
      <button
        className={cn(
          className,
          '-mr-3 flex h-14 w-14 flex-shrink-0 items-center justify-center truncate rounded-none border-transparent bg-transparent py-3 text-foreground-ui-navbar focus:outline-none active:border-transparent',
        )}
        onClick={handleOpen}
        aria-label="View account"
      >
        {!isUserFilledIcon ? (
          <UserCircleOutlineIcon />
        ) : (
          <UserFilledIcon width={20} height={20} className="flex-shrink-0" />
        )}
      </button>

      <DrawerContainer isOpen={open} onClose={handleClose} anchor="right">
        <GuestProfileMenu
          venueSlug={venueSlug}
          venueId={venueId}
          venueName={venueName}
          handleClose={handleClose}
        />
      </DrawerContainer>
    </>
  )
}
